'use client';
import React, { useState } from 'react';

interface Testimonial {
  quote: string;
  name: string;
  company: string;
  role: string;
  gender: 'male' | 'female';
  initials: string;
  color: string;
}

const testimonials: Testimonial[] = [
  {
    quote: 'Celsoft experience has been an eye-opener, expanding my skill base in dealing with Excel Spreadsheets. I recommend it to anyone who is in finance.',
    name: 'Alfred Mandara',
    company: 'Reserve Bank of Zimbabwe',
    role: 'Finance Professional',
    gender: 'male',
    initials: 'AM',
    color: '#1f525d',
  },
  {
    quote: 'A very necessary and well-structured course, with necessary concepts learnt, which are very applicable to my daily work practices. Well done, team Celsoft!',
    name: 'Lucy Chabikisa',
    company: 'Zimnat Insurance',
    role: 'Finance Analyst',
    gender: 'female',
    initials: 'LC',
    color: '#6c2d30',
  },
  {
    quote: 'A rich program that all finance professionals require. I recommend all ACCA and CIMA students to take the course — it consolidates your accounting and finance skills.',
    name: 'Onias Chikwata',
    company: 'UNDP',
    role: 'Financial Controller',
    gender: 'male',
    initials: 'OC',
    color: '#2d4a8a',
  },
  {
    quote: 'Every person involved in financial accounting and using Excel on a day-to-day basis should consider this course to save on labour and time.',
    name: 'Kumbirai Fusai',
    company: 'UNDSS',
    role: 'Senior Accountant',
    gender: 'male',
    initials: 'KF',
    color: '#1f525d',
  },
];

export default function TestimonialsSection() {
  const [active, setActive] = useState(0);

  const current = testimonials[active];

  return (
    <section id="testimonials" className="py-24 bg-white overflow-hidden">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <div className="tag-pill bg-[#8dc53e]/15 text-[#6fa030] mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#8dc53e]" />
              Client Voices
            </div>
            <h2 className="font-bold text-[#0f0f0f] text-section-title">
              What our clients say<br />
              <span className="text-[#1f525d]">about working with us.</span>
            </h2>
          </div>
          {/* Navigation arrows */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setActive((a) => (a - 1 + testimonials.length) % testimonials.length)}
              className="w-11 h-11 rounded-xl border border-[#dde5ec] flex items-center justify-center text-[#5a6a72] hover:border-[#1f525d] hover:text-[#1f525d] transition-all duration-200"
              aria-label="Previous testimonial"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <span className="text-sm text-[#5a6a72] font-medium">{active + 1} / {testimonials.length}</span>
            <button
              onClick={() => setActive((a) => (a + 1) % testimonials.length)}
              className="w-11 h-11 rounded-xl border border-[#dde5ec] flex items-center justify-center text-[#5a6a72] hover:border-[#1f525d] hover:text-[#1f525d] transition-all duration-200"
              aria-label="Next testimonial"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
          {/* Main testimonial */}
          <div
            className="rounded-2xl p-8 lg:p-10 relative overflow-hidden"
            key={active}
            style={{ background: 'linear-gradient(135deg, #f7f9fb 0%, #edf2f6 100%)', border: '1px solid #e8edf2' }}
          >
            {/* Decorative quote */}
            <div
              className="absolute top-6 right-8 text-[8rem] font-serif leading-none select-none pointer-events-none"
              style={{ color: current.color, opacity: 0.06 }}
            >
              &ldquo;
            </div>

            {/* Stars */}
            <div className="flex gap-1 mb-6">
              {[1,2,3,4,5].map((s) => (
                <svg key={s} width="18" height="18" viewBox="0 0 24 24" fill="#8dc53e">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              ))}
            </div>

            <blockquote
              className="text-[#0f0f0f] leading-relaxed mb-8 relative z-10"
              style={{ fontSize: 'clamp(1rem, 1.5vw, 1.2rem)', fontWeight: 400 }}
            >
              &ldquo;{current.quote}&rdquo;
            </blockquote>

            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-sm"
                style={{ background: current.color }}
              >
                {current.initials}
              </div>
              <div>
                <p className="font-bold text-[#0f0f0f] text-sm">{current.name}</p>
                <p className="text-xs text-[#5a6a72]">{current.role} · {current.company}</p>
              </div>
              <div
                className="ml-auto px-3 py-1.5 rounded-lg text-xs font-semibold"
                style={{ background: `${current.color}15`, color: current.color }}
              >
                Verified Client
              </div>
            </div>
          </div>

          {/* Side: all testimonials mini list */}
          <div className="flex flex-col gap-3">
            {testimonials.map((t, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`text-left rounded-xl p-4 transition-all duration-200 ${
                  i === active
                    ? 'shadow-md'
                    : 'hover:bg-[#f7f9fb]'
                }`}
                style={{
                  background: i === active ? 'white' : 'transparent',
                  border: i === active ? `1px solid ${t.color}40` : '1px solid #e8edf2',
                  boxShadow: i === active ? `0 4px 20px ${t.color}15` : 'none',
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-xs shrink-0"
                    style={{ background: t.color }}
                  >
                    {t.initials}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-[#0f0f0f] truncate">{t.name}</p>
                    <p className="text-xs text-[#5a6a72] truncate">{t.company}</p>
                  </div>
                  {i === active && (
                    <div className="ml-auto shrink-0 w-2 h-2 rounded-full" style={{ background: t.color }} />
                  )}
                </div>
              </button>
            ))}

            {/* Trust badge */}
            <div
              className="rounded-xl p-4 mt-auto"
              style={{ background: 'linear-gradient(135deg, #1f525d, #2d6b7a)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <p className="text-white font-bold text-sm mb-1">45-Day Guarantee</p>
              <p className="text-white/60 text-xs">Not satisfied? Full refund, no questions asked.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}